<template>
  <div class="search-destination">
    <Input
      id="destinationStation"
      :label="$t('travelPlanner.search.destination')"
      :internalLabel="true"
      v-model="inputValue"
      :placeholder="$t('travelPlanner.search.destination')"
      :icon="marker"
      :hasDelete="true"
      :suggestions="filteredSuggestions"
      @focus="fetchDestinationSuggestions"
      @select-suggestion="handleSelectSuggestion" />
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, computed, watch } from 'vue'
  import { useTravelSearchStore } from '@/stores/travelSearchStore'
  import { getSortedStationSuggestions, SortableStation } from '@/utils/stationSortingUtils'
  import { pushInteractionToDatalayer } from '@/services/gaLoggingService'
  import Input from '@/components/BaseComponents/Input.vue'
  import marker from '@/assets/icons/marker.svg'
  import { Station } from '@/types/stationTypes'
  import { getSearchHistory, addToSearchHistory } from '@/composables/useSearchHistory'

  type Suggestion = {
    uicCode: string
    name: string
  }

  export default defineComponent({
    name: 'SearchDestination',
    components: {
      Input,
    },
    setup() {
      const travelSearchStore = useTravelSearchStore()
      const inputValue = ref(travelSearchStore.destinationStation.name || '')
      const showSuggestions = ref(false)
      const selectedUICCode = ref('')

      const fetchDestinationSuggestions = () => {
        // Only enable suggestions if we have an origin station
        if (travelSearchStore.OriginStation._u_i_c_station_code) {
          showSuggestions.value = true
        } else {
          // Show a message or handle the case where origin isn't selected
          inputValue.value = ''
        }
      }

      const handleSelectSuggestion = (code: string) => {
        selectedUICCode.value = code
        showSuggestions.value = false
        const fromSuggestions = travelSearchStore.mappedDestinationSuggestions.find(
          (suggestion) => suggestion.uicCode === code,
        )
        const name = fromSuggestions?.name ?? getSearchHistory('destinationSearchHistory').find((h) => h.code === code)?.name
        if (name) {
          travelSearchStore.setDestinationStation({ name, _u_i_c_station_code: code })
          travelSearchStore.setOriginDestination(travelSearchStore.origin, name)
          inputValue.value = name
          addToSearchHistory('destinationSearchHistory', { name, code })
          pushInteractionToDatalayer('add destination', { selection: name })
        }
      }

      const onSuggestionKeydown = (suggestion: Suggestion, event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          handleSelectSuggestion(suggestion.uicCode)
        }
      }

      const filteredSuggestions = computed(() => {
        if (!inputValue.value) {
          if (!travelSearchStore.OriginStation._u_i_c_station_code) return []
          return getSearchHistory('destinationSearchHistory').filter(
            (h) => h.code !== travelSearchStore.OriginStation._u_i_c_station_code,
          )
        }
        const stationsFromSuggestions: SortableStation[] = travelSearchStore.mappedDestinationSuggestions
          .filter((suggestion) =>
            suggestion.name.toLowerCase().includes(inputValue.value.toLowerCase()),
          )
          .map((suggestion) => ({
            name: suggestion.name,
            _u_i_c_station_code: suggestion.uicCode,
            sequence_number: suggestion.sequence_number,
            district: suggestion.district,
            county: suggestion.county,
          }))

        return getSortedStationSuggestions(stationsFromSuggestions, inputValue.value)
      })

      watch(inputValue, (newVal) => {
        if (!newVal) {
          showSuggestions.value = false
          travelSearchStore.setOriginDestination(travelSearchStore.origin, '')
        } else {
          showSuggestions.value = true
        }
      })

      watch(
        () => travelSearchStore.destinationStation,
        (newDestinationStation) => {
          if (newDestinationStation) {
            inputValue.value = newDestinationStation.name
            travelSearchStore.setOriginDestination(travelSearchStore.origin, newDestinationStation.name)
          }
        },
        { immediate: true },
      )

      return {
        inputValue,
        showSuggestions,
        fetchDestinationSuggestions,
        handleSelectSuggestion,
        marker,
        onSuggestionKeydown,
        filteredSuggestions,
        selectedUICCode,
      }
    },
  })
</script>
